import React, { useEffect, useState, useMemo } from 'react';
import { Box, TextField, Typography, Checkbox, Modal, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonSelect from '../../../../form-components/CommonSelect';
import CommonLoading from '../../../../form-components/CommonLoading';
import { updateGroupLessonTemplate, getTemplateLinkedClasses } from '../../../../api/lesson';
import TicketSelectModal from './TicketSelectModal';

import {
  EditContainer,
  ModalHeader,
  ModalHeaderTitle,
  EditContent,
  FormRow,
  FormField,
  FormFieldLabel,
  AppliedLessonsHeader,
  AppliedLessonsTitle,
  AppliedLessonsNote,
  AppliedLessonsTable,
  ModalFooter,
} from './GroupLessonTemplateStyled';

const TemplateEditModal = ({ open, onClose, template, tickets = [] }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);

  const [form, setForm] = useState({
    templateName: '',
    classDuration: '',
    ticketDeductionCount: '',
    membersMaxCount: '',
    membersWaitMaxCount: '',
    ticketIds: [],
  });
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [filterTrainer, setFilterTrainer] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [filterClassName, setFilterClassName] = useState('');

  // 적용될 수업 목록 조회 (GET /templates/{id}/classes)
  const { data: linkedClasses = [], isLoading } = useQuery({
    queryKey: ['templateLinkedClasses', template?.id],
    queryFn: () => getTemplateLinkedClasses(template.id, user.token),
    enabled: !!template?.id && !!user?.token && open,
  });

  useEffect(() => {
    if (open && template) {
      setForm({
        templateName: template.templateName || '',
        className: template.className || '',
        classDuration: template.classDuration?.toString() || '',
        ticketDeductionCount: template.ticketDeductionCount?.toString() || '',
        membersMaxCount: template.membersMaxCount?.toString() || '',
        membersWaitMaxCount: template.membersWaitMaxCount?.toString() || '',
        ticketIds: template.ticketIds || [],
      });
      setSelectedRowIndices([]);
      setConfirmOpen(false);
      setFilterTrainer('');
      setFilterRoom('');
      setFilterClassName('');
    }
  }, [open, template]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value.replace(/[^0-9]/g, '') }));
  };

  const handleToggleRow = index => {
    setSelectedRowIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index],
    );
  };

  const handleToggleAll = () => {
    if (selectedRowIndices.length === filteredClasses.length) {
      setSelectedRowIndices([]);
    } else {
      setSelectedRowIndices(filteredClasses.map((_, i) => i));
    }
  };

  // 필터링된 목록
  const filteredClasses = useMemo(() => {
    return linkedClasses.filter(cls => {
      if (filterClassName && !cls.className?.toLowerCase().includes(filterClassName.toLowerCase())) return false;
      if (filterTrainer && cls.trainerName !== filterTrainer) return false;
      if (filterRoom && cls.exerciseRoomName !== filterRoom) return false;
      return true;
    });
  }, [linkedClasses, filterClassName, filterTrainer, filterRoom]);

  // 필터 옵션
  const trainerOptions = useMemo(() =>
    [...new Set(linkedClasses.map(c => c.trainerName).filter(Boolean))]
      .map(name => ({ value: name, label: name })),
    [linkedClasses]);
  const roomOptions = useMemo(() =>
    [...new Set(linkedClasses.map(c => c.exerciseRoomName).filter(Boolean))]
      .map(name => ({ value: name, label: name })),
    [linkedClasses]);

  // 선택된 행들의 courseClassTimeId 수집 (filteredClasses 기준 인덱스)
  const selectedClassTimeIds = selectedRowIndices
    .map(i => filteredClasses[i]?.courseClassTimeId)
    .filter(Boolean);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateGroupLessonTemplate(id, data, user.token),
    onSuccess: () => {
      toast.success('그룹수업이 수정되었습니다');
      queryClient.invalidateQueries({ queryKey: ['groupLessonTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!form.templateName.trim()) {
      toast.error('그룹수업명을 입력해주세요');
      return;
    }

    if (selectedRowIndices.length > 0) {
      const selectedClasses = selectedRowIndices.map(i => filteredClasses[i]);
      const maxReserved = Math.max(...selectedClasses.map(c => c.currentReservedCount || 0));
      const maxWaiting = Math.max(...selectedClasses.map(c => c.currentWaitCount || 0));

      if (form.membersMaxCount && Number(form.membersMaxCount) < maxReserved) {
        toast.error(`선택된 수업 중 현재 예약 인원(${maxReserved}명)보다 적은 정원을 설정할 수 없습니다`);
        return;
      }
      if (form.membersWaitMaxCount && Number(form.membersWaitMaxCount) < maxWaiting) {
        toast.error(`선택된 수업 중 현재 대기 인원(${maxWaiting}명)보다 적은 대기 정원을 설정할 수 없습니다`);
        return;
      }
    }

    setConfirmOpen(true);
  };

  const handleConfirmSubmit = () => {
    setConfirmOpen(false);
    updateMutation.mutate({
      id: template.id,
      data: {
        templateName: form.templateName.trim(),
        className: form.className?.trim() || null,
        classDuration: Number(form.classDuration) || 50,
        ticketDeductionCount: Number(form.ticketDeductionCount) || 1,
        membersMaxCount: Number(form.membersMaxCount) || 0,
        membersWaitMaxCount: Number(form.membersWaitMaxCount) || 0,
        ticketIds: form.ticketIds,
        applyClassTimeIds: selectedClassTimeIds,
      },
    });
  };

  const selectedTicketCount = form.ticketIds.length;
  const allSelected = filteredClasses.length > 0 && selectedRowIndices.length === filteredClasses.length;

  return (
    <>
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <EditContainer>
        <ModalHeader>
          <ModalHeaderTitle>그룹수업 수정</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </ModalHeader>

        <EditContent>
          {/* 그룹수업명 + 수강권 */}
          <FormRow>
            <FormField>
              <FormFieldLabel>그룹수업명 <span style={{ color: '#ef5350' }}>*</span></FormFieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.templateName}
                onChange={e => handleChange('templateName', e.target.value)}
              />
            </FormField>
            <FormField>
              <FormFieldLabel>수강권 선택</FormFieldLabel>
              <Box
                onClick={() => setTicketModalOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '40px',
                  px: '14px',
                  border: '1px solid #c4c4c4',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: selectedTicketCount > 0 ? '#f0f8f8' : '#fff',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' },
                }}>
                <Typography sx={{ fontSize: '14px', fontWeight: selectedTicketCount > 0 ? 500 : 400, color: selectedTicketCount > 0 ? '#4AB3BC' : '#999' }}>
                  {selectedTicketCount > 0 ? `${selectedTicketCount}개 수강권 선택됨` : '수강권을 선택해 주세요'}
                </Typography>
                <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
              </Box>
            </FormField>
          </FormRow>

          {/* 수업 시간 + 차감 + 정원 + 대기 */}
          <FormRow>
            <FormField>
              <FormFieldLabel>수업 시간</FormFieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.classDuration}
                onChange={e => handleNumberChange('classDuration', e.target.value)}
                InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>분</span> }}
              />
            </FormField>
            <FormField>
              <FormFieldLabel>차감 횟수</FormFieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.ticketDeductionCount}
                onChange={e => handleNumberChange('ticketDeductionCount', e.target.value)}
                InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>회</span> }}
              />
            </FormField>
            <FormField>
              <FormFieldLabel>정원</FormFieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.membersMaxCount}
                onChange={e => handleNumberChange('membersMaxCount', e.target.value)}
                InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }}
              />
            </FormField>
            <FormField>
              <FormFieldLabel>대기 정원</FormFieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.membersWaitMaxCount}
                onChange={e => handleNumberChange('membersWaitMaxCount', e.target.value)}
                InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }}
              />
            </FormField>
          </FormRow>

          {/* 적용될 수업 테이블 */}
          <AppliedLessonsHeader>
            <AppliedLessonsTitle>현재 그룹수업이 적용된 수업 목록 ({filteredClasses.length}건{filteredClasses.length !== linkedClasses.length ? ` / 전체 ${linkedClasses.length}건` : ''})</AppliedLessonsTitle>
            <AppliedLessonsNote>
              *수정 시 변경사항을 적용할 수업을 선택해 주세요. (앞으로 진행될 수업만 노출)
            </AppliedLessonsNote>
          </AppliedLessonsHeader>

          {/* 필터 */}
          {linkedClasses.length > 0 && (
            <Box sx={{
              display: 'flex', gap: '8px', alignItems: 'center', mb: '8px', flexWrap: 'wrap',
              '& .MuiOutlinedInput-root': { height: '32px', fontSize: '13px', borderRadius: '6px' },
            }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#333', minWidth: '30px' }}>필터</Typography>
              <TextField
                size="small"
                placeholder="수업명 검색"
                value={filterClassName}
                onChange={e => { setFilterClassName(e.target.value); setSelectedRowIndices([]); }}
                sx={{ width: '150px', '& .MuiOutlinedInput-root': { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' } } }}
              />
              <CommonSelect value={filterTrainer} onChange={e => { setFilterTrainer(e.target.value); setSelectedRowIndices([]); }}
                options={trainerOptions} emptyLabel="강사" width={110} />
              <CommonSelect value={filterRoom} onChange={e => { setFilterRoom(e.target.value); setSelectedRowIndices([]); }}
                options={roomOptions} emptyLabel="강의실" width={130} />
              {(filterClassName || filterTrainer || filterRoom) && (
                <Typography
                  onClick={() => { setFilterClassName(''); setFilterTrainer(''); setFilterRoom(''); setSelectedRowIndices([]); }}
                  sx={{ fontSize: '12px', color: '#aaa', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { color: '#666' } }}>
                  필터 초기화
                </Typography>
              )}
            </Box>
          )}

          <AppliedLessonsTable>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedRowIndices.length > 0 && !allSelected}
                    onChange={handleToggleAll}
                    sx={{ p: 0, color: '#999', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } }}
                  />
                </th>
                <th>수업일</th>
                <th>수업 시간</th>
                <th>수업명</th>
                <th>강사</th>
                <th>강의실</th>
                <th>예약 인원</th>
                <th>대기 인원</th>
                <th>수강권</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9}>
                    <CommonLoading label="수업 목록 불러오는 중..." minHeight={120} />
                  </td>
                </tr>
              ) : filteredClasses.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '32px', color: '#999' }}>
                    {linkedClasses.length === 0 ? '적용된 수업이 없습니다' : '검색 결과가 없습니다'}
                  </td>
                </tr>
              ) : (
                filteredClasses.map((cls, index) => (
                  <tr key={cls.courseClassTimeId || index}>
                    <td>
                      <Checkbox
                        size="small"
                        checked={selectedRowIndices.includes(index)}
                        onChange={() => handleToggleRow(index)}
                        sx={{ p: 0, color: '#999', '&.Mui-checked': { color: '#4AB3BC' } }}
                      />
                    </td>
                    <td>{cls.nearestClassDate}</td>
                    <td>{cls.nearestStartTime}~{cls.nearestEndTime}</td>
                    <td>{cls.className}</td>
                    <td>{cls.trainerName}</td>
                    <td>{cls.exerciseRoomName}</td>
                    <td>{cls.currentReservedCount}명</td>
                    <td>{cls.currentWaitCount}명</td>
                    <td>{cls.selectedTicketCount}개</td>
                  </tr>
                ))
              )}
            </tbody>
          </AppliedLessonsTable>
        </EditContent>

        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '수정 중...' : '수정 완료'}
          </CommonModalButton>
        </ModalFooter>
      </EditContainer>
    </Modal>

    <TicketSelectModal
      open={ticketModalOpen}
      onClose={() => setTicketModalOpen(false)}
      tickets={tickets}
      selectedIds={form.ticketIds}
      onChangeIds={ids => handleChange('ticketIds', ids)}
    />

    {/* 수정 확인 다이얼로그 */}
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>수정 확인</DialogTitle>
      <DialogContent>
        {selectedClassTimeIds.length > 0 ? (
          <>
            <Typography sx={{ fontSize: '14px', color: '#333', mb: '12px' }}>
              아래 <b style={{ color: '#4AB3BC' }}>{selectedClassTimeIds.length}건</b>의 수업에 변경사항이 적용됩니다.
            </Typography>
            <Box sx={{ maxHeight: '240px', overflow: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9f9f9' }}>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee' }}>수업일</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee' }}>시간</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee' }}>수업명</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee' }}>강사</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRowIndices.map(i => {
                    const cls = filteredClasses[i];
                    if (!cls) return null;
                    return (
                      <tr key={cls.courseClassTimeId}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{cls.nearestClassDate}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{cls.nearestStartTime}~{cls.nearestEndTime}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{cls.className}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>{cls.trainerName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: '14px', color: '#333' }}>
            선택된 수업이 없습니다. 그룹수업 정보만 수정됩니다.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '12px 24px' }}>
        <CommonModalButton variant="cancel" onClick={() => setConfirmOpen(false)}>취소</CommonModalButton>
        <CommonModalButton variant="submit" onClick={handleConfirmSubmit} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? '수정 중...' : '수정 완료'}
        </CommonModalButton>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TemplateEditModal;
